/**
 * AUTOMAÇÕES — a área única.
 *
 * ## Por que ela existe
 *
 * O owner tentou montar uma automação e se perdeu: *"estou achando as telas super confusas, pouco
 * intuitivas… não sei quando devo usar um ou o outro, nem a diferença real dos dois, ou se vou usar
 * junto"*. O produto tinha **duas entradas de menu** — *Agente Hugi* e *Fluxos* — para uma coisa só,
 * e nenhuma das duas dizia como a outra se encaixava. Escolha dele em 05/09, entre três: **uma área
 * só, Automações**, com o assistente virando um recurso dentro dela. Desenho aprovado em
 * `docs/previas/automacoes.html`.
 *
 * ## As três coisas que esta tela existe para dizer, e que nenhuma das duas dizia
 *
 * 1. **A automação é `quando` → `o que`.** A criação passou a começar pelo *quando*, que é a
 *    pergunta que faltava: montava-se o roteiro inteiro e só na hora de publicar se descobria em que
 *    momento ele rodaria — ou que aquele gatilho não servia àquela forma.
 * 2. **A forma muda o comportamento, e agora está na lista.** Duas automações com o mesmo gatilho e
 *    formas diferentes se comportam de modos opostos, e a lista antiga mostrava as duas iguais.
 * 3. **Quem chama quem.** O assistente aparece **abaixo** das automações, e cada um diz por quais
 *    automações é chamado. A ordem na tela é a ordem em que o produto decide: as automações primeiro,
 *    o assistente com número **depois** — e só quando nenhuma respondeu.
 *
 * ## O que NÃO mudou, e é o que garante que nada quebrou
 *
 * Os dois editores são **os mesmos**: `EditorDeFluxo` e `Agente`, movidos para dentro daqui. Nenhuma
 * rota de escrita mudou, nenhuma migration entrou, e desfazer esta frente é devolver duas entradas à
 * barra. O que se juntou foi a **entrada**, não o motor.
 */

import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Atalho, Botao, EstadoCarregando, EstadoDaFalha, Escolha, Ladrilho, falhaDe, useSessao, type TomDoLadrilho } from "@hugi/ui";
import {
  BlocoDoCadastro,
  CabecalhoDaTela,
  CampoDoCadastro,
  Dialogo,
  Dica,
  GradeDeCartoes,
  ListView,
  PaginaDeCadastro,
  PaginaInteira,
  Selo,
  type ColunaDaLista,
  type Tom,
  type VistaDaLista,
} from "@hugi/ui/kit";
import { Bot, CalendarClock, GitBranch, ListOrdered, MessageSquareText, MoonStar, Plus, RotateCcw, Sparkles } from "lucide-react";
import {
  criarAgente,
  criarFluxo,
  useAutomacoes,
  useCatalogos,
  type Catalogos,
  type Assistente,
  type Automacao,
  type FormaDaAutomacao,
  type PassoCompleto,
  type TipoDeGatilho,
} from "./dados.js";
import {
  Biblioteca,
  EditorDeFluxo,
  EtapaDoFunilParaOGatilho,
  MotivoDeEncerramento,
  QUANDO,
  ROTULO_DO_GATILHO,
} from "./Fluxo.js";
import { Agente, seloDoAssistente } from "./Agente.js";
import { gatilhoCombinaComAForma } from "../../../src/canal/fluxo.js";
import "./automacoes.css";

/* ================================================================================================
   AS SEMENTES — o que a automação nasce sendo, por forma escolhida.

   Elas são **vazias de conteúdo e cheias de tipo**: o que a escolha da forma decide é o COMPORTAMENTO
   (menu abre na primeira mensagem; roteiro anda em qualquer momento), não as frases. Semear frase
   seria pôr texto nosso na boca da empresa — e a pessoa publicaria sem ler.
   ============================================================================================== */

type FormaEscolhida = "menu" | "roteiro" | "assistente";

function sementeDa(forma: FormaEscolhida): PassoCompleto[] {
  if (forma === "menu") {
    return [{ chave: "inicio", tipo: "menu", pergunta: "", opcoes: [] }];
  }
  if (forma === "roteiro") {
    return [{ chave: "inicio", tipo: "mensagem", pergunta: "", opcoes: [], proximo: null }];
  }
  return [
    {
      chave: "assistente",
      tipo: "agente",
      pergunta: "",
      opcoes: [],
      agente: { agenteId: null, objetivo: "", saidasEsperadas: [], maxTurnos: 3, aoEstourar: null },
      proximo: null,
    },
  ];
}

const FORMAS: readonly { id: FormaEscolhida; rotulo: string; frase: string }[] = [
  {
    id: "menu",
    rotulo: "Menu de opções",
    frase: "Pergunta o assunto e oferece respostas numeradas. Cada opção manda para uma fila.",
  },
  {
    id: "roteiro",
    rotulo: "Roteiro de passos",
    frase: "Mensagem, pergunta, condição, espera, ação, sistema externo — na ordem que você montar.",
  },
  {
    id: "assistente",
    rotulo: "Entregar ao assistente",
    frase: "Um passo só: o assistente conversa até resolver, com um objetivo e uma saída.",
  },
];

/* ================================================================================================
   AS COLUNAS DAS DUAS LISTAS (spec 22: Consulta — `ListView` do kit)
   ============================================================================================== */

const ROTULO_DA_FORMA: Readonly<Record<FormaDaAutomacao, string>> = {
  menu: "Menu",
  roteiro: "Roteiro",
  vazia: "Sem passos",
};

/** O tom do selo da v2 a partir do tom de sempre (o mesmo par cor + texto). */
const TOM: Readonly<Record<"ok" | "degradado" | "neutro", Tom>> = { ok: "sucesso", degradado: "atencao", neutro: "neutro" };

const dataCurta = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

function gatilhoDa(a: Automacao): string {
  const gatilho = ROTULO_DO_GATILHO[a.quando] ?? a.quando;
  return a.quando === "mensagem_contendo" && a.quandoValor ? `${gatilho} "${a.quandoValor}"` : gatilho;
}

function estadoDaAutomacao(a: Automacao): { rotulo: string; tom: "ok" | "degradado" | "neutro" } {
  return a.quando !== "nunca"
    ? { rotulo: "Atendendo", tom: "ok" }
    : a.temRascunho
      ? { rotulo: "Rascunho", tom: "degradado" }
      : { rotulo: "Desligada", tom: "neutro" };
}

/**
 * O estado do assistente na lista: o MESMO critério da tela dele (`seloDoAssistente`, spec 23) — publicação e
 * horário juntos. Aqui a lista só sabe QUANTOS números, então o selo diz "No ar em 2 números", nunca o número.
 */
function estadoDoAssistente(a: Assistente): { rotulo: string; tom: "ok" | "degradado" | "neutro" } {
  const s = seloDoAssistente({ numeros: Array.from({ length: a.publicacoes }, () => "um número"), atende: a.atende });
  return { rotulo: a.publicacoes === 1 && s.estado === "ok" ? "No ar em 1 número" : s.rotulo, tom: s.estado };
}

const ROTULO_DO_ATENDE: Readonly<Record<Assistente["atende"], string>> = {
  nunca: "Desligado",
  fora_do_horario: "Fora do horário",
  sempre: "Sempre",
};

const seloDe = (e: { rotulo: string; tom: "ok" | "degradado" | "neutro" }): ReactNode => <Selo tom={TOM[e.tom]}>{e.rotulo}</Selo>;

const COLUNAS_DA_AUTOMACAO: ColunaDaLista<Automacao>[] = [
  { id: "nome", rotulo: "Nome", celula: (a) => a.nome, ordenar: (a) => a.nome, link: true, principal: true },
  { id: "quando", rotulo: "Quando", celula: gatilhoDa, ordenar: gatilhoDa, dica: gatilhoDa },
  { id: "estado", rotulo: "Estado", celula: (a) => seloDe(estadoDaAutomacao(a)), ordenar: (a) => estadoDaAutomacao(a).rotulo },
  {
    id: "forma",
    rotulo: "O que ela faz",
    quebra: true,
    celula: (a) => (
      <span className="hugi-automacoes-forma">
        <strong>{ROTULO_DA_FORMA[a.forma]}</strong>
        <span>{a.resumo}</span>
      </span>
    ),
  },
  {
    id: "rodou",
    rotulo: "Rodou (30 d)",
    numerica: true,
    ordenar: (a) => a.rodou30d,
    // "—" e não "0": zero rodadas de uma automação DESLIGADA é o esperado, e um zero em vermelho
    // ao lado de "Desligada" mandaria alguém investigar o que está funcionando.
    celula: (a) => (a.quando === "nunca" && a.rodou30d === 0 ? "—" : `${a.rodou30d}×`),
  },
  { id: "criado", rotulo: "Criada", numerica: true, celula: (a) => dataCurta(a.criadoEm), ordenar: (a) => a.criadoEm },
];

/** As vistas são os estados de sempre (o filtro "Estado" da listagem antiga). */
const VISTAS_DA_AUTOMACAO: VistaDaLista<Automacao>[] = [
  { id: "todas", rotulo: "Todas", passa: () => true },
  { id: "atendendo", rotulo: "Atendendo", passa: (a) => estadoDaAutomacao(a).rotulo === "Atendendo" },
  { id: "rascunho", rotulo: "Rascunho", passa: (a) => estadoDaAutomacao(a).rotulo === "Rascunho" },
  { id: "desligada", rotulo: "Desligadas", passa: (a) => estadoDaAutomacao(a).rotulo === "Desligada" },
];

const COLUNAS_DO_ASSISTENTE: ColunaDaLista<Assistente>[] = [
  { id: "nome", rotulo: "Nome", celula: (a) => a.nome, ordenar: (a) => a.nome, link: true, principal: true },
  { id: "estado", rotulo: "Estado", celula: (a) => seloDe(estadoDoAssistente(a)), ordenar: (a) => estadoDoAssistente(a).rotulo },
  // "Onde" agora está no selo; a coluna diz o QUANDO, a outra metade de "responde sozinho".
  { id: "quando", rotulo: "Quando responde", celula: (a) => ROTULO_DO_ATENDE[a.atende], ordenar: (a) => ROTULO_DO_ATENDE[a.atende] },
  {
    id: "usos",
    rotulo: "Usado por",
    quebra: true,
    celula: (a) =>
      a.usadoPor.length === 0 ? "—" : a.usadoPor.length === 1 ? a.usadoPor[0]!.nome : `${a.usadoPor.length} automações`,
  },
  { id: "criado", rotulo: "Criado", numerica: true, celula: (a) => dataCurta(a.criadoEm), ordenar: (a) => a.criadoEm },
];

/* ================================================================================================
   A TELA
   ============================================================================================== */

/** O que a tela está mostrando. A URL manda; isto é só a leitura dela. */
type Aberto =
  | { readonly o: "lista" }
  | { readonly o: "nova" }
  | { readonly o: "modelo" }
  | { readonly o: "automacao"; readonly id: string }
  | { readonly o: "assistente"; readonly id: string };

export function Automacoes(props: { readonly aberto: Aberto }) {
  const [, navegar] = useLocation();
  const q = useAutomacoes();
  const sessao = useSessao();
  const podeMexerNoAssistente = sessao.permissoes.includes("manage_integration");
  const podeMexerNaAutomacao = sessao.permissoes.includes("manage_catalog");

  if (q.isPending) return <EstadoCarregando rotulo="Carregando as automações" />;
  if (q.isError) return <EstadoDaFalha falha={falhaDe(q.error)!} aoRepetir={() => void q.refetch()} />;

  const voltar = () => navegar("/automacoes");

  if (props.aberto.o === "automacao") {
    /*
      ⚠️ O GATILHO ESCOLHIDO NO ASSISTENTE VIAJA PELA URL, e isso foi achado ABRINDO A TELA.

      A primeira versão passava a escolha por estado do componente — e o assistente NAVEGA para o
      editor, então o estado morria na troca de rota: a pessoa escolhia "quando a mensagem contém",
      criava, e o painel de publicar abria em "Desligado". A pergunta que o assistente existe para
      fazer era engolida no passo seguinte. Nenhum teste pegaria: os dois componentes estavam certos
      isoladamente.

      Na URL, ela sobrevive à navegação e ao F5, e é descartável — o editor só a usa quando a
      automação nunca foi publicada.
    */
    const p = new URLSearchParams(typeof window === "undefined" ? "" : window.location.search);
    const quando = p.get("quando");
    const gatilhoInicial = quando ? { quando: quando as TipoDeGatilho, valor: p.get("valor") ?? "" } : null;
    return <EditorDeFluxo fluxoId={props.aberto.id} aoVoltar={voltar} gatilhoInicial={gatilhoInicial} />;
  }
  if (props.aberto.o === "assistente") {
    const id = props.aberto.id;
    const a = q.data.assistentes.find((x) => x.id === id);
    return (
      <Agente
        agenteId={id}
        aoVoltar={voltar}
        usadoPor={a?.usadoPor ?? []}
        aoAbrirAutomacao={(fluxoId) => navegar(`/automacoes/${fluxoId}`)}
      />
    );
  }
  if (props.aberto.o === "nova") {
    return (
      <NovaAutomacao
        aoAbrir={(id, quando, valor) => {
          const q = new URLSearchParams({ quando, ...(valor ? { valor } : {}) });
          navegar(`/automacoes/${id}?${q.toString()}`);
        }}
        aoCancelar={voltar}
      />
    );
  }
  if (props.aberto.o === "modelo") {
    return (
      <PaginaInteira rotulo="Usar um modelo" classe="hugi-automacoes">
        <CabecalhoDaTela
          icone={<GitBranch size={18} />}
          titulo="Usar um modelo"
          dica="Cada modelo entra como rascunho, e nada dele começa a atender sozinho."
        />
        {/* A biblioteca rola DENTRO do cartão: o cabeçalho e a volta ficam (spec 22). */}
        <div className="v2-cartao v2-elastico hugi-automacoes-rolagem" role="region" aria-label="Modelos" tabIndex={0}>
          <Biblioteca
            aoImportar={(id, gatilho) => {
              // O gatilho SUGERIDO do modelo (C4) viaja pela URL como o do assistente de criação: pré-seleciona, não liga.
              const busca = gatilho ? `?${new URLSearchParams({ quando: gatilho.quando, ...(gatilho.valor ? { valor: gatilho.valor } : {}) }).toString()}` : "";
              navegar(`/automacoes/${id}${busca}`);
            }}
            aoAbrirAssistente={(id) => navegar(`/automacoes/assistente/${id}`)}
            aoFechar={voltar}
          />
        </div>
      </PaginaInteira>
    );
  }

  const motivoDeCriar = podeMexerNaAutomacao ? undefined : "Só quem administra os catálogos cria automação.";
  const assistenteEmFoco = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("mostrar") === "assistentes";
  const principal = q.data.assistentes.find((a) => a.principal) ?? q.data.assistentes[0] ?? null;

  const listaDeAutomacoes = (
    <ListView<Automacao>
      titulo="Automações"
      icone={<GitBranch size={18} />}
      colunas={COLUNAS_DA_AUTOMACAO}
      linhas={q.data.automacoes}
      chave={(a) => a.id}
      vistas={VISTAS_DA_AUTOMACAO}
      memoria="hugi:automacoes:lista"
      busca={{ campos: (a) => [a.nome, gatilhoDa(a), a.quandoValor, a.resumo], placeholder: "Buscar automação" }}
      abrir={{ href: (a) => `/automacoes/${a.id}`, aoAbrir: (a) => navegar(`/automacoes/${a.id}`) }}
      contagem={(n) => (n === 1 ? "1 automação" : `${n} automações`)}
      vazio={{ semNada: "Nenhuma automação ainda. Comece por um dos ladrilhos acima." }}
      acoes={
        <>
          <Dica texto="Uma automação é: quando algo acontece, o Hugi faz alguma coisa." />
          {/* SECUNDÁRIOS (spec 23): a porta principal é o ladrilho pronto; montar do zero é para quem já sabe. */}
          <Botao
            tipo="secundario"
            onClick={() => navegar("/automacoes/nova")}
            desabilitado={!podeMexerNaAutomacao}
            {...(motivoDeCriar ? { motivoDeDesabilitar: motivoDeCriar } : {})}
          >
            Nova automação
          </Botao>
        </>
      }
    />
  );

  const listaDeAssistentes = (
    <ListView<Assistente>
      titulo="Assistentes"
      icone={<Sparkles size={18} />}
      colunas={COLUNAS_DO_ASSISTENTE}
      linhas={q.data.assistentes}
      chave={(a) => a.id}
      memoria="hugi:automacoes:assistentes"
      busca={{ campos: (a) => [a.nome, ...a.usadoPor.map((f) => f.nome)], placeholder: "Buscar assistente" }}
      abrir={{
        href: (a) => `/automacoes/assistente/${a.id}`,
        aoAbrir: (a) => navegar(`/automacoes/assistente/${a.id}`),
      }}
      contagem={(n) => (n === 1 ? "1 assistente" : `${n} assistentes`)}
      vazio={{ semNada: "Nenhum assistente." }}
      acoes={
        <>
          {/*
            A ORDEM ENTRE OS DOIS, dita em uma frase — no (i), e não em parágrafo.

            É a pergunta do owner — *"não sei… se vou usar junto"* — e a resposta é: usa junto, nesta
            ordem. Sem ela, quem publica um assistente num número e uma automação de primeira mensagem
            espera que os dois respondam, vê um só, e conclui que um está quebrado.
          */}
          <Dica
            texto={
              "Quem conversa quando não há trilho. Uma automação pode chamar um deles num passo. " +
              "O assistente publicado num número responde depois das automações — só quando nenhuma delas " +
              "respondeu àquela mensagem." +
              (!podeMexerNoAssistente ? " Editar assistente exige a permissão de integrações, que o seu acesso não tem." : "")
            }
          />
          {/*
            NOVO ASSISTENTE (spec 23): `criarAgente` só existia escondido no seletor da tela antiga do Agente — pela
            área de Automações não havia como criar um.
          */}
          <NovoAssistente
            desabilitado={!podeMexerNoAssistente}
            aoCriar={(id) => navegar(`/automacoes/assistente/${id}?aba=comportamento`)}
          />
        </>
      }
    />
  );

  /*
    "O QUE O HUGI FAZ SOZINHO?" NO TOPO (spec 23, jornada 2), e as duas listas embaixo, na MESMA tela (05/09: a
    ordem na tela é a ordem em que o produto decide — automações primeiro, o assistente com número depois). Cada
    lista rola por dentro; a página não. Com `?mostrar=assistentes` (o item "Assistentes" do menu), a lista de
    assistentes sobe e fica com a parte maior — as automações continuam ali, embaixo.
  */
  return (
    <PaginaInteira rotulo="Automações" classe="hugi-automacoes">
      {podeMexerNaAutomacao && !assistenteEmFoco ? (
        <OQueOHugiFazSozinho
          automacoes={q.data.automacoes}
          principal={principal}
          aoAbrirAutomacao={(id, quando, valor) => {
            const busca = new URLSearchParams({ quando, ...(valor ? { valor } : {}) });
            navegar(`/automacoes/${id}?${busca.toString()}`);
          }}
          aoAbrirAssistente={(id) => navegar(`/automacoes/assistente/${id}?aba=quando&atende=fora_do_horario`)}
          aoVerModelos={() => navegar("/automacoes/modelos")}
          aoDoZero={() => navegar("/automacoes/nova")}
        />
      ) : null}
      <GradeDeCartoes
        rotulo="Automações e assistentes"
        {...(assistenteEmFoco ? { classe: "is-assistentes-primeiro" } : {})}
        areas={assistenteEmFoco ? '"assistentes" "automacoes"' : '"automacoes" "assistentes"'}
        colunas="minmax(0, 1fr)"
        linhas="minmax(0, 3fr) minmax(0, 2fr)"
        cartoes={(assistenteEmFoco
          ? [
              { id: "assistentes", titulo: "Assistentes", area: "assistentes", proprio: true, conteudo: listaDeAssistentes },
              { id: "automacoes", titulo: "Automações", area: "automacoes", proprio: true, conteudo: listaDeAutomacoes },
            ]
          : [
              { id: "automacoes", titulo: "Automações", area: "automacoes", proprio: true, conteudo: listaDeAutomacoes },
              { id: "assistentes", titulo: "Assistentes", area: "assistentes", proprio: true, conteudo: listaDeAssistentes },
            ])}
      />
    </PaginaInteira>
  );
}

/* ================================================================================================
   O QUE O HUGI FAZ SOZINHO? — os ladrilhos prontos (spec 23, jornada 2)

   Criar uma automação custava 22 cliques e ~14 conceitos: escolher gatilho, forma, nome, montar os passos em
   branco, achar o gatilho de novo na aba de publicar. Cada ladrilho aqui traz o gatilho, a forma e um modelo de
   passos JÁ PREENCHIDOS e leva direto ao editor para revisar. Continua nascendo DESLIGADA (decisão de 05/09): o
   gatilho viaja pela URL até o editor e só vale quando a pessoa clica em Ligar.

   ⚠️ O texto dos modelos é nosso, e a pessoa vai lê-lo antes de ligar — é por isso que ele é curto e genérico, e
   que as filas ficam em branco (o editor aponta o que falta depois que ela mexer).
   ============================================================================================== */

interface Receita {
  readonly id: string;
  readonly nome: string;
  readonly estado: string;
  readonly tom: TomDoLadrilho;
  readonly icone: ReactNode;
  readonly quando: TipoDeGatilho;
  /** O parâmetro do gatilho, procurado nos catálogos da empresa; vazio = a pessoa escolhe no editor. */
  readonly valor: (c: Catalogos | undefined) => string;
  readonly passos: () => PassoCompleto[];
}

const procurar = (lista: readonly { id: string; nome: string }[] | undefined, re: RegExp): string =>
  (lista ?? []).find((x) => re.test(x.nome.normalize("NFD").replace(/[\u0300-\u036f]/g, "")))?.id ?? "";

export const RECEITAS: readonly Receita[] = [
  {
    id: "menu",
    nome: "Menu na primeira mensagem",
    estado: "Pergunta o assunto e manda para a fila certa",
    tom: "fx",
    icone: <ListOrdered size={18} />,
    quando: "primeira_mensagem",
    valor: () => "",
    passos: () => [
      {
        chave: "inicio",
        tipo: "menu",
        pergunta: "Olá! Para agilizar, escolha uma opção:\n\n1 - Agendar\n2 - Preços e orçamento\n3 - Outro assunto",
        opcoes: [
          { quando: "1", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
          { quando: "2", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
          { quando: "3", vaiPara: null, filaId: null, etiquetaId: null, encerra: true },
        ],
      },
    ],
  },
  {
    id: "orcamento",
    nome: "Responder \"orçamento\"",
    estado: "Quem pede orçamento fala com o assistente",
    tom: "ia",
    icone: <MessageSquareText size={18} />,
    quando: "mensagem_contendo",
    valor: () => "orçamento",
    passos: () => [
      { chave: "inicio", tipo: "mensagem", pergunta: "Oi! Já vou te ajudar com o orçamento.", opcoes: [], proximo: "assistente" },
      {
        chave: "assistente",
        tipo: "agente",
        pergunta: "",
        opcoes: [],
        agente: {
          agenteId: null,
          objetivo: "Entender o que a pessoa quer orçar e responder com o que está no material. Se faltar informação, passar para uma pessoa.",
          saidasEsperadas: ["resolvido", "precisa_de_gente"],
          maxTurnos: 4,
          aoEstourar: null,
        },
        proximo: null,
      },
    ],
  },
  {
    id: "reagendar",
    nome: "Reagendar quem faltou",
    estado: "Convida para um novo horário",
    tom: "ag",
    icone: <CalendarClock size={18} />,
    quando: "motivo_de_encerramento",
    valor: (c) => procurar(c?.motivos, /falt|compareceu|no.?show|ausen/i),
    passos: () => [
      {
        chave: "inicio",
        tipo: "mensagem",
        pergunta: "Oi! Sentimos sua falta. Quer marcar um novo horário? É só responder por aqui.",
        opcoes: [],
        proximo: null,
      },
    ],
  },
  {
    id: "retomar",
    nome: "Retomar orçamento",
    estado: "Volta a falar um dia depois",
    tom: "rl",
    icone: <RotateCcw size={18} />,
    quando: "etiqueta_aplicada",
    valor: (c) => procurar(c?.etiquetas, /orcamento/i),
    passos: () => [
      { chave: "inicio", tipo: "esperar", pergunta: "", opcoes: [], esperarMin: 60 * 24, proximo: "retomar" },
      {
        chave: "retomar",
        tipo: "mensagem",
        pergunta: "Oi! Passando para saber se ficou alguma dúvida sobre o orçamento.",
        opcoes: [],
        proximo: null,
      },
    ],
  },
];

/** Nome livre na empresa: "Menu na primeira mensagem", "Menu na primeira mensagem 2"… (o nome é único por empresa). */
function nomeLivre(base: string, existentes: readonly { nome: string }[]): string {
  const usados = new Set(existentes.map((a) => a.nome.trim().toLowerCase()));
  if (!usados.has(base.toLowerCase())) return base;
  for (let i = 2; ; i++) if (!usados.has(`${base} ${i}`.toLowerCase())) return `${base} ${i}`;
}

function OQueOHugiFazSozinho(props: {
  readonly automacoes: readonly Automacao[];
  readonly principal: Assistente | null;
  readonly aoAbrirAutomacao: (id: string, quando: TipoDeGatilho, valor: string) => void;
  readonly aoAbrirAssistente: (id: string) => void;
  readonly aoVerModelos: () => void;
  readonly aoDoZero: () => void;
}) {
  const cliente = useQueryClient();
  const catalogos = useCatalogos();
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const usar = async (r: Receita) => {
    setOcupado(r.id);
    setErro(null);
    const criado = await criarFluxo(cliente, { nome: nomeLivre(r.nome.replace(/"/g, ""), props.automacoes), passos: r.passos() });
    setOcupado(null);
    if (!criado.ok || !criado.id) {
      setErro(criado.falha?.erro.mensagem ?? "Não foi possível criar agora.");
      return;
    }
    props.aoAbrirAutomacao(criado.id, r.quando, r.valor(catalogos.data));
  };

  const [menu, orcamento, reagendar, retomar] = RECEITAS as readonly [Receita, Receita, Receita, Receita];
  return (
    <section className="v2-cartao hugi-automacoes-sozinho" aria-labelledby="hugi-automacoes-sozinho-titulo">
      <header>
        <h2 id="hugi-automacoes-sozinho-titulo">O que o Hugi faz sozinho?</h2>
        <span className="hugi-automacoes-sozinho-acoes">
          <button type="button" className="v2-botao" onClick={props.aoVerModelos}>Ver todos os modelos</button>
          <button type="button" className="v2-botao" onClick={props.aoDoZero}>Do zero</button>
        </span>
      </header>
      <div className="hugi-automacoes-ladrilhos">
        {[menu, orcamento, reagendar].map((r) => (
          <Atalho key={r.id} tom={r.tom} icone={r.icone} nome={r.nome} estado={ocupado === r.id ? "Criando…" : r.estado} aoClicar={() => void usar(r)} />
        ))}
        {/* Fora do horário é o ASSISTENTE, não um fluxo: o ladrilho abre o principal já com a escolha feita (D2). */}
        <Atalho
          tom="ia"
          icone={<MoonStar size={18} />}
          nome="Responder fora do horário (IA)"
          estado={props.principal ? `O assistente ${props.principal.nome} responde` : "Crie um assistente primeiro"}
          {...(props.principal ? { aoClicar: () => props.aoAbrirAssistente(props.principal!.id) } : {})}
        />
        <Atalho key={retomar.id} tom={retomar.tom} icone={retomar.icone} nome={retomar.nome} estado={ocupado === retomar.id ? "Criando…" : retomar.estado} aoClicar={() => void usar(retomar)} />
      </div>
      {erro ? <p role="alert" className="v2-erro">{erro}</p> : null}
    </section>
  );
}

/** "Novo assistente": só o nome; o resto se configura nas abas dele. */
function NovoAssistente(props: { readonly desabilitado: boolean; readonly aoCriar: (id: string) => void }) {
  const cliente = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const criar = async () => {
    setOcupado(true);
    setErro(null);
    const r = await criarAgente(cliente, { nome: nome.trim() });
    setOcupado(false);
    if (!r.ok || !r.id) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível criar o assistente.");
      return;
    }
    setAberto(false);
    setNome("");
    props.aoCriar(r.id);
  };

  return (
    <>
      <Botao
        tipo="secundario"
        onClick={() => setAberto(true)}
        desabilitado={props.desabilitado}
        {...(props.desabilitado ? { motivoDeDesabilitar: "Criar assistente exige a permissão de integrações." } : {})}
      >
        <Plus size={16} /> Novo assistente
      </Botao>
      <Dialogo
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        titulo="Novo assistente"
        largura="estreita"
        rodape={
          <>
            <button type="button" className="v2-botao" onClick={() => setAberto(false)}>Cancelar</button>
            <button type="button" className="v2-botao is-primario" disabled={ocupado || nome.trim() === ""} onClick={() => void criar()}>
              {ocupado ? "Criando…" : "Criar e abrir"}
            </button>
          </>
        }
      >
        <label className="v2-cadastro-campo">
          <span>Nome</span>
          <input
            className="campo"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && nome.trim() !== "") void criar();
            }}
            placeholder="Cobrança"
            autoFocus
          />
        </label>
        {erro ? <p role="alert" className="v2-erro">{erro}</p> : null}
      </Dialogo>
    </>
  );
}

/* ================================================================================================
   NOVA AUTOMAÇÃO — quando, depois o que
   ============================================================================================== */

/**
 * O assistente de criação, na ordem do desenho: **quando** primeiro, **forma** depois.
 *
 * ## A forma incompatível aparece RECUSADA, com o motivo — não some
 *
 * Quem escolhe *"a mensagem contém uma palavra"* e depois *"menu"* está montando um par que o motor
 * não honra, e até 05/09 o produto deixava montar e recusava só no botão Publicar, dias depois. A
 * regra que decide é `gatilhoCombinaComAForma`, do domínio — a MESMA que `publicarFluxo` usa como
 * portão. Esconder a opção seria pior que desabilitá-la: quem a procura conclui que o produto não a
 * tem.
 *
 * ## O gatilho não é gravado aqui
 *
 * A automação nasce **desligada**, como sempre nasceu. A escolha do *quando* viaja até o painel de
 * publicar do editor — ver `EditorDeFluxo`.
 */
function NovaAutomacao(props: {
  readonly aoAbrir: (id: string, quando: TipoDeGatilho, valor: string) => void;
  readonly aoCancelar: () => void;
}) {
  const cliente = useQueryClient();
  const catalogos = useCatalogos();
  const [quando, setQuando] = useState<TipoDeGatilho | null>(null);
  const [valor, setValor] = useState("");
  const [forma, setForma] = useState<FormaEscolhida | null>(null);
  const [nome, setNome] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  // "Desligado" não é um quando: é o estado em que toda automação nasce.
  const gatilhos = QUANDO.filter((g) => g.id !== "nunca");
  const escolhido = gatilhos.find((g) => g.id === quando) ?? null;

  const criar = async () => {
    if (!quando || !forma) return;
    setOcupado(true);
    setErro(null);
    const r = await criarFluxo(cliente, { nome: nome.trim(), passos: sementeDa(forma) });
    setOcupado(false);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível criar.");
      return;
    }
    if (r.id) props.aoAbrir(r.id, quando, valor.trim());
  };

  const motivo = !quando
    ? "Escolha quando ela deve agir."
    : !forma
      ? "Escolha o que ela faz."
      : nome.trim() === ""
        ? "Dê um nome à automação."
        : null;

  /*
    CADASTRO (spec 22): trilha, cabeçalho e rodapé fixos; só o corpo rola. A ordem dos blocos é a do desenho —
    quando, depois o que, depois o nome —, e cada bloco só aparece quando o anterior foi respondido.
    O Enter no nome envia pelo `<form>` do kit (antes era um `onKeyDown` no campo).
  */
  return (
    <PaginaDeCadastro
      icone={<GitBranch size={18} />}
      titulo="Nova automação"
      dica="O começo é sempre o quando: é ele que decide quais formas fazem sentido."
      trilha={[{ rotulo: "Automações", href: "/automacoes", aoClicar: props.aoCancelar }]}
      rotuloDaPrimaria="Criar e abrir"
      aoSalvar={() => void criar()}
      aoCancelar={props.aoCancelar}
      salvando={ocupado}
      motivo={motivo}
      erro={erro}
    >
      {/* --- 1 · QUANDO --------------------------------------------------------------------------- */}
      <BlocoDoCadastro titulo="Quando o Hugi deve agir?" largo>
        <div className="hugi-automacoes-escolhas is-duas">
          {gatilhos.map((g) => (
            <Escolha
              key={g.id}
              nome="quando"
              valor={g.id}
              marcada={quando === g.id}
              aoEscolher={() => {
                setQuando(g.id);
                // Trocar de gatilho limpa o parâmetro: um id de etiqueta guardado num gatilho de texto
                // é campo fantasma. Mesma decisão do painel de publicar.
                setValor("");
                setForma(null);
              }}
              titulo={g.rotulo}
              descricao={g.frase}
            />
          ))}
        </div>

        {escolhido?.valor === "texto" && (
          <CampoDoCadastro
            rotulo="A palavra ou expressão"
            ajuda="Compara sem acento e sem diferenciar maiúscula. Sem palavra, ela dispararia em toda mensagem."
          >
            {(p) => <input {...p} value={valor} onChange={(e) => setValor(e.target.value)} placeholder="orçamento" />}
          </CampoDoCadastro>
        )}
        {escolhido?.valor === "etiqueta" && (
          <CampoDoCadastro rotulo="Qual etiqueta">
            {(p) => (
              <select {...p} value={valor} onChange={(e) => setValor(e.target.value)}>
                <option value="">escolha uma etiqueta</option>
                {(catalogos.data?.etiquetas ?? []).map((et) => (
                  <option key={et.id} value={et.id}>
                    {et.nome}
                  </option>
                ))}
              </select>
            )}
          </CampoDoCadastro>
        )}
        {escolhido?.valor === "motivo" && (
          <div className="v2-cadastro-campo">
            <MotivoDeEncerramento valor={valor || null} aoEscolher={(v) => setValor(v ?? "")} />
          </div>
        )}
        {escolhido?.valor === "etapa" && (
          <div className="v2-cadastro-campo">
            <EtapaDoFunilParaOGatilho valor={valor || null} aoEscolher={(v) => setValor(v ?? "")} />
          </div>
        )}
      </BlocoDoCadastro>

      {/* --- 2 · O QUE ELA FAZ -------------------------------------------------------------------- */}
      {quando ? (
        <BlocoDoCadastro titulo="E o que ela faz?" dica="Só as formas que este gatilho aceita ficam disponíveis." largo>
          <div className="hugi-automacoes-escolhas is-tres">
            {FORMAS.map((f) => {
              const combina = gatilhoCombinaComAForma(quando, sementeDa(f.id));
              return (
                <div key={f.id} className="hugi-automacoes-forma-opcao">
                  <Escolha
                    nome="forma"
                    valor={f.id}
                    marcada={forma === f.id}
                    aoEscolher={() => combina.serve && setForma(f.id)}
                    desabilitada={!combina.serve}
                    titulo={f.rotulo}
                    descricao={f.frase}
                  />
                  {!combina.serve ? <p className="hugi-automacoes-recusa">{combina.porque}</p> : null}
                </div>
              );
            })}
          </div>
        </BlocoDoCadastro>
      ) : null}

      {/* --- 3 · O NOME --------------------------------------------------------------------------- */}
      {quando && forma ? (
        <BlocoDoCadastro titulo="Como ela se chama">
          <CampoDoCadastro
            rotulo="Nome"
            obrigatorio
            largo
            ajuda="Ela nasce desligada. Nada começa a atender antes de você publicar."
          >
            {(p) => (
              <input
                {...p}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Pedido de orçamento"
                autoFocus
              />
            )}
          </CampoDoCadastro>
        </BlocoDoCadastro>
      ) : null}
    </PaginaDeCadastro>
  );
}

/** O ladrilho do assistente, para quem importar esta tela precisar dele. */
export function LadrilhoDeAssistente() {
  return (
    <Ladrilho tom="ia">
      <Bot size={18} />
    </Ladrilho>
  );
}
